import { popoverPlacementClass, type PopoverPlacement } from "../workspace/popover-placement";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Icon, IconButton, Input, Row, Stack, useToast } from "@wato/ui";
import { useApi, useErrorMessage } from "../../api/context";
import type { AutomationPauseState } from "../../api/types";
import { useWorkspace } from "../workspace/WorkspaceContext";

interface PauseCtx {
  readonly state: AutomationPauseState | null;
  readonly busy: boolean;
  readonly canEdit: boolean;
  run(action: () => Promise<AutomationPauseState>, done: string): Promise<boolean>;
}
const Ctx = createContext<PauseCtx | null>(null);

/** Single shared read of the pause state for the top-bar chip and the "all paused" banner. */
export function AutomationPauseProvider({ children }: { readonly children: ReactNode }) {
  const api = useApi();
  const toast = useToast();
  const errorMessage = useErrorMessage();
  const { workspace } = useWorkspace();
  const [state, setState] = useState<AutomationPauseState | null>(null);
  const [busy, setBusy] = useState(false);
  const canEdit = workspace.permissions.granted?.includes("workspace.edit") ?? false;

  const load = useCallback(async () => {
    try {
      setState(await api.getAutomationPause(workspace.id));
    } catch {
      setState(null); // best-effort: a failed read must not break the shell
    }
  }, [api, workspace.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (action: () => Promise<AutomationPauseState>, done: string) => {
      setBusy(true);
      try {
        setState(await action());
        toast.show({ tone: "success", title: done });
        return true;
      } catch (caught) {
        toast.show({ tone: "error", title: errorMessage(caught) });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [toast, errorMessage],
  );

  return <Ctx.Provider value={{ state, busy, canEdit, run }}>{children}</Ctx.Provider>;
}

function useAutomationPause(): PauseCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("AutomationPauseProvider missing");
  return ctx;
}

/**
 * WP-223 master switch banner: persistent red banner (every workspace page) while ALL automation
 * is paused, with Resume for members who may edit. Partial pauses (AI / channel) and the pause
 * actions live in the top-bar `AutomationStatusChip` (UX-19) instead of stacked banners.
 */
export function MasterSwitch() {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const { state, busy, canEdit, run } = useAutomationPause();
  const api = useApi();
  if (!state?.workspace.paused) return null;
  return (
    <Alert tone="error" title={t("automationPause.bannerTitle")}>
      <Stack gap={2}>
        <p>{t("automationPause.bannerBody")}</p>
        {state.workspace.reason ? (
          <p>{t("automationPause.bannerReason", { reason: state.workspace.reason })}</p>
        ) : null}
        {canEdit ? (
          <Row>
            <IconButton
              label={t("automationPause.resume")}
              icon={<Icon name="play" />}
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api.resumeAutomation(workspace.id),
                  t("automationPause.resumedToast"),
                )
              }
            />
          </Row>
        ) : null}
      </Stack>
    </Alert>
  );
}

/** UX-19: ONE "Automation status" chip in the top bar; its menu holds every pause/resume control. */
export function AutomationStatusChip({
  compact = false,
  placement = "end-down",
}: { compact?: boolean; placement?: PopoverPlacement } = {}) {
  const { t } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const { state, busy, canEdit, run } = useAutomationPause();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  if (!state) return null;

  const allPaused = state.workspace.paused;
  const partial = state.ai.paused || state.pausedChannels.length > 0;
  const status = allPaused ? "paused" : partial ? "partial" : "running";
  const tone = {
    paused: "bg-danger-bg text-fg",
    partial: "bg-warning-bg text-fg",
    running: "bg-surface-sunken text-fg-muted",
  }[status];

  const dot = { paused: "bg-danger-icon", partial: "bg-warning-icon", running: "bg-success-icon" }[
    status
  ];
  const label = t("automationPause.statusChip", {
    status: t(`automationPause.status.${status}`),
  });

  const act = async (action: () => Promise<AutomationPauseState>, done: string) => {
    if (await run(action, done)) {
      setAsking(false);
      setReason("");
    }
  };

  function submitPause(event: FormEvent) {
    event.preventDefault();
    void act(
      () => api.pauseAutomation(workspace.id, reason.trim() || undefined),
      t("automationPause.pausedToast"),
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
        className={
          compact
            ? "inline-flex size-9 items-center justify-center rounded-md hover:bg-surface-sunken"
            : `rounded-full px-3 py-1.5 text-sm ${tone}`
        }
        title={label}
        data-status={status}
      >
        {compact ? (
          <>
            <span
              aria-hidden="true"
              className={`size-2.5 rounded-full ${dot}`}
              data-testid="automation-dot"
            />
            <span className="sr-only">{label}</span>
          </>
        ) : (
          label
        )}
      </button>
      {open ? (
        <div
          role="group"
          aria-label={t("automationPause.statusMenu")}
          className={`absolute z-40 w-72 rounded-md border border-line bg-surface p-3 shadow-lg ${popoverPlacementClass(placement)}`}
        >
          <Stack gap={2}>
            {state.ai.paused ? (
              <Row justify="between" gap={2}>
                <span className="text-sm">{t("automationPause.aiBannerTitle")}</span>
                {canEdit ? (
                  <IconButton
                    label={t("automationPause.aiResume")}
                    icon={<Icon name="play" />}
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => api.resumeAi(workspace.id),
                        t("automationPause.aiResumedToast"),
                      )
                    }
                  />
                ) : null}
              </Row>
            ) : null}
            {state.pausedChannels.map((c) => (
              <Row key={c.id} justify="between" gap={2}>
                <span className="text-sm">
                  {t("automationPause.channelPaused", { channel: c.displayName ?? c.id })}
                </span>
                {canEdit ? (
                  <IconButton
                    label={t("automationPause.channelResume")}
                    icon={<Icon name="play" />}
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => api.resumeChannelAutomation(workspace.id, c.id),
                        t("automationPause.channelResumedToast"),
                      )
                    }
                  />
                ) : null}
              </Row>
            ))}
            {status === "running" ? (
              <p className="text-sm text-fg-muted">{t("automationPause.runningBody")}</p>
            ) : null}
            {canEdit && !allPaused && !state.ai.paused ? (
              <Button
                size="sm"
                variant="ghost"
                loading={busy}
                onClick={() =>
                  void act(() => api.pauseAi(workspace.id), t("automationPause.aiPausedToast"))
                }
              >
                {t("automationPause.aiPause")}
              </Button>
            ) : null}
            {canEdit && !allPaused && !asking ? (
              <Button size="sm" variant="ghost" onClick={() => setAsking(true)}>
                {t("automationPause.pauseAll")}
              </Button>
            ) : null}
            {canEdit && !allPaused && asking ? (
              <form aria-label={t("automationPause.pauseAll")} onSubmit={submitPause}>
                <Stack gap={2}>
                  <Input
                    label={t("automationPause.reasonLabel")}
                    value={reason}
                    maxLength={500}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Row gap={2}>
                    <Button type="submit" size="sm" loading={busy}>
                      {t("automationPause.confirmPause")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setAsking(false)}
                    >
                      {t("automationPause.cancel")}
                    </Button>
                  </Row>
                </Stack>
              </form>
            ) : null}
          </Stack>
        </div>
      ) : null}
    </div>
  );
}
